// -----------------------------------------------------------------------------
// ui/place-clip.ts — add a library asset onto the timeline.
//
// Shared by the media panel (click → append at the end of a track) and the
// timeline's drag-and-drop (drop → at a specific time/track). Picks (or creates)
// a compatible track, builds the clip, and dispatches it as one undoable command.
// -----------------------------------------------------------------------------
import { AddClipCommand } from '../core/commands/clip-commands';
import { AddTrackCommand } from '../core/commands/track-commands';
import { type ID, type MediaAsset, type MediaClip, clipEnd, findTrack, newId } from '../core/model';
import type { EditorContext } from './context';

const IMAGE_DEFAULT_DURATION = 5; // seconds for a still on the timeline

export interface PlaceOptions {
  /** Where to start the clip; omitted → append after the last clip on the track. */
  startSec?: number;
  /** Preferred target track (e.g. the one a clip was dropped on). */
  trackId?: ID;
}

export function placeAsset(ctx: EditorContext, asset: MediaAsset, opts: PlaceOptions = {}): void {
  const project = ctx.store.state.project;
  const wantsAudioTrack = asset.kind === 'audio';

  // Resolve the target track: honour a compatible preferred track, else the
  // first track of the right kind, else create one.
  let track = opts.trackId ? findTrack(project, opts.trackId) : undefined;
  if (track && (track.kind === 'audio') !== wantsAudioTrack) track = undefined;
  if (!track) {
    track = project.tracks.find((t) => (wantsAudioTrack ? t.kind === 'audio' : t.kind === 'video'));
  }
  if (!track) {
    const add = new AddTrackCommand(
      wantsAudioTrack ? 'audio' : 'video',
      wantsAudioTrack ? 'Audio 1' : 'Video 1',
      0,
    );
    ctx.store.dispatch(add);
    track = findTrack(project, add.trackId);
  }
  if (!track) return;

  const duration = asset.kind === 'image' ? IMAGE_DEFAULT_DURATION : Math.max(0.1, asset.durationSec);
  const start =
    opts.startSec != null
      ? Math.max(0, opts.startSec)
      : track.clips.reduce((max, c) => Math.max(max, clipEnd(c)), 0);

  const clip: MediaClip = {
    id: newId(),
    kind: asset.kind === 'image' ? 'image' : asset.kind === 'audio' ? 'audio' : 'video',
    name: asset.name,
    startSec: start,
    durationSec: duration,
    assetId: asset.id,
    inSec: 0,
  };
  ctx.store.dispatch(new AddClipCommand(track.id, clip));
  ctx.store.select(clip.id);
  ctx.setStatus(`Added “${asset.name}”.`);
}
